import { useMutation } from '@apollo/client';
import { produce } from 'immer';
import { v4 } from 'uuid';

import { cacheUtils } from '@eluve/apollo-client';
import { useToast } from '@eluve/components';
import { useTranslation } from '@eluve/i18n';

import {
  acceptBillingCodeRecommendationMutation,
  appointmentBillingCodesFragment,
} from './AppointmentBillingCodes.operations';
import { MedicalCodeType } from './useEditBillingCodes';

export const useBillingCodeRecommendations = ({
  appointmentId,
}: {
  appointmentId: string;
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [acceptRecommendation] = useMutation(
    acceptBillingCodeRecommendationMutation,
    {
      onError: () =>
        toast.error(t('appointmentTools.common.acceptRecommendationFailed')),
      optimisticResponse: ({
        appointmentId,
        billingCodeId,
        modifiers,
        price,
      }) => ({
        insertAppointmentBillingCodesOne: {
          __typename: 'AppointmentBillingCodes' as const,
          id: v4(),
          modifiers: modifiers ?? null,
          price: price ?? null,
        },
        updateAppointmentBillingCodeRecommendations: {
          returning: [
            {
              __typename: 'AppointmentBillingCodeRecommendations' as const,
              id: v4(),
              appointmentId,
              codeId: billingCodeId,
              isFinal: true,
            },
          ],
        },
      }),
    },
  );

  const onAcceptRecommendation = async (medicalCode: MedicalCodeType) => {
    await acceptRecommendation({
      variables: {
        appointmentId,
        billingCodeId: medicalCode.id,
        price: medicalCode.price ?? null,
        modifiers: medicalCode.modifiers ?? null,
        quantity: 1,
        userGroupCodeConfigId: medicalCode?.userGroupCodeConfigId ?? null,
      },
      update: (_cache, { data }) => {
        const insert = data?.insertAppointmentBillingCodesOne;
        cacheUtils.updateFragment(
          {
            fragment: appointmentBillingCodesFragment,
            key: {
              id: appointmentId,
            },
          },
          (existing) => {
            const updatedId = data?.insertAppointmentBillingCodesOne?.id;
            if (!existing || !updatedId) {
              return existing;
            }

            return produce(existing, (draft) => {
              draft.billingCodes.push({
                __typename: 'AppointmentBillingCodes',
                id: updatedId,
                price: insert?.price ?? null,
                quantity: 1,
                modifiers: insert?.modifiers ?? null,
                linked_codes: [],
                description: medicalCode.description ?? null,
                medical_code: {
                  __typename: 'MedicalCodes',
                  ...medicalCode,
                },
              });
            });
          },
        );
      },
    });
  };

  return {
    onAcceptRecommendation,
  };
};
